import { type ListInventoryItemsFn } from '@/features/inventory'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { action, observable } from 'mobx'
import { switchMap } from 'rxjs'
import type { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { shareLatest, whileActive, withAbortSignal } from '@/utils/rx-util'
import { type CustomItemToRemove } from '@/features/jobs/details/components/UpdateJobScopeDialog/UpdateJobScopeDialogViewModel'
import { JobScopeCustomItemViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/ListItems/JobScopeCustomItem/JobScopeCustomItemViewModel'
import { JobScopeInventoryItemViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/ListItems/JobScopeInventoryItem/JobScopeInventoryItemViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { shallowItemsAreEqual } from '@/utils/array-util'
import { type Job } from '@/features/jobs/Job'

/**
 * Whether an existing service or a new custom one is being added.
 */
export type ManageItemsMode = 'AnExistingService' | 'NewCustomService'

export interface ManageItemsViewModelOptions {
  /**
   * The job whose scope is managed.
   */
  job: Job

  /**
   * The workspace whose services can be added.
   */
  workspaceId: Id

  /**
   * Searches the workspace's services.
   */
  searchServiceItems: ListInventoryItemsFn

  /**
   * Offers retrying a failed service search.
   */
  createAlertRetryWrapper: AlertRetryWrapperFactory

  /**
   * Called for each service added on submit.
   */
  onAddInventoryItem: (item: JobScopeInventoryItemViewModel) => void

  /**
   * Called when an added service is taken out again.
   */
  onSetInventoryItems: (items: JobScopeInventoryItemViewModel[]) => void

  /**
   * Called on submit with the custom services to keep.
   */
  onSetCustomItems: (items: JobScopeCustomItemViewModel[]) => void

  /**
   * Called on submit for each custom service already on the job that was removed.
   */
  onRemoveCustomItem: (id: Id) => void

  /**
   * Called on reset so the owner can hand back the items it holds.
   */
  onReset: () => void
}

/**
 * Adds services to, and removes custom services from, the job scope being
 * updated.
 */
export class ManageItemsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The service search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Pages through the services matching the search text.
   */
  readonly inventoryItemScrollProvider =
    new InfiniteScrollProvider<InventoryItem>({
      limit: 50,
      fetchItems: async ({ paging, signal }) => {
        const withRetry = this._options.createAlertRetryWrapper(
          'Loading services failed',
          signal,
        )

        const results = await withRetry(() =>
          this._options.searchServiceItems(
            {
              workspaceIds: this._options.workspaceId
                ? [this._options.workspaceId]
                : [],
              limit: paging.limit,
              searchText: this.searchText.value,
              after: paging.after ?? undefined,
              archivedStatus: 'Active',
              proVisibilityStatus: 'All',
              jobType: this._options.job.jobType,
              orderBy: 'name',
            },
            signal,
          ),
        )

        const filteredResults = results.data.filter(
          (item) =>
            !this.selectedInventoryItems.find(
              (selectedItem) => selectedItem.versionId === item.versionId,
            ),
        )

        return {
          data: filteredResults,
          next: results.next,
        }
      },
    })

  /**
   * Refetches the services whenever the search text settles.
   */
  readonly query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() =>
      withAbortSignal((signal) =>
        this.inventoryItemScrollProvider
          .fetch({ signal })
          .catch(() => undefined),
      ),
    ),
    shareLatest(),
  )

  /**
   * Whether an existing service or a new custom one is being added.
   */
  @observable
  accessor mode: ManageItemsMode = 'AnExistingService'

  /**
   * The services picked to be added.
   */
  @observable
  accessor inventoryItems: InventoryItem[] = []

  /**
   * The services the job scope already holds.
   */
  @observable
  accessor selectedInventoryItems: JobScopeInventoryItemViewModel[] = []

  /**
   * The custom services the job scope holds.
   */
  @observable
  accessor selectedCustomItems: JobScopeCustomItemViewModel[] = []

  /**
   * The custom services already on the job that were removed.
   */
  @observable
  accessor selectedCustomItemsToRemove: CustomItemToRemove[] = []

  constructor(private readonly _options: ManageItemsViewModelOptions) {
    this._lifecycle.manage(this.query$.pipe(whileActive(this.modal.showing$)))
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Sets the services picked to be added.
   */
  @action.bound
  setInventoryItems(inventoryItems: InventoryItem[]) {
    if (shallowItemsAreEqual(this.inventoryItems, inventoryItems)) {
      return
    }
    this.inventoryItems = inventoryItems
  }

  /**
   * Sets the services the job scope already holds.
   */
  @action.bound
  setSelectedInventoryItems(inventoryItems: JobScopeInventoryItemViewModel[]) {
    if (shallowItemsAreEqual(this.selectedInventoryItems, inventoryItems)) {
      return
    }
    this.selectedInventoryItems = inventoryItems
  }

  /**
   * Sets the custom services the job scope holds.
   */
  @action.bound
  setSelectedCustomItems(customItems: JobScopeCustomItemViewModel[]) {
    if (shallowItemsAreEqual(this.selectedCustomItems, customItems)) {
      return
    }
    this.selectedCustomItems = customItems
  }

  /**
   * Adds an empty custom service.
   */
  @action.bound
  createCustomItem() {
    const newCustomItem = new JobScopeCustomItemViewModel('Added')
    this.selectedCustomItems.push(newCustomItem)
  }

  /**
   * Marks a custom service already on the job as removed.
   */
  @action.bound
  addSelectedCustomItemToRemove(customItem: CustomItemToRemove) {
    this.selectedCustomItemsToRemove.push(customItem)
  }

  /**
   * Removes a custom service.
   */
  @action.bound
  removeSelectedCustomItem(customInventoryItem: JobScopeCustomItemViewModel) {
    if (customInventoryItem.source === 'Job') {
      this.addSelectedCustomItemToRemove({
        id: customInventoryItem.id,
      })
    }

    this.selectedCustomItems = this.selectedCustomItems.filter(
      (item) => item !== customInventoryItem,
    )
  }

  /**
   * Clears the picks and takes the owner's items again.
   */
  @action.bound
  reset() {
    this.searchText.clear()
    this.mode = 'AnExistingService'
    this.selectedInventoryItems = []
    this.selectedCustomItems = []
    this.selectedCustomItemsToRemove = []
    this.inventoryItems = []

    this._options.onReset()
  }

  /**
   * Sets whether an existing or a custom service is being added.
   */
  @action.bound
  setMode(mode: ManageItemsMode) {
    this.mode = mode
  }

  /**
   * Hands the picks to the owner and closes the dialog.
   */
  @action.bound
  submit() {
    let hasValidationErrors = false
    this.selectedCustomItems.forEach((item) => {
      if (!item.validate()) {
        hasValidationErrors = true
      }
    })
    if (hasValidationErrors) {
      return
    }

    this.inventoryItems.forEach((item) => {
      const inventoryItem = new JobScopeInventoryItemViewModel(item, (i) => {
        this._options.onSetInventoryItems(
          this.selectedInventoryItems.filter(
            (selectedItem) => selectedItem.versionId !== i.versionId,
          ),
        )
      })
      this._options.onAddInventoryItem(inventoryItem)
    })

    this.selectedCustomItemsToRemove.forEach((item) => {
      this._options.onRemoveCustomItem(item.id)
    })

    this._options.onSetCustomItems(this.selectedCustomItems.slice())

    this.modal.close()
  }
}
